import {
  Component,
  Input,
  OnInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
} from '@angular/core';
import { AgentEntity } from '@wephone-core/model/entity/agent';
import { AuthenticationService } from '@wephone-core/service/authentication';
import * as _ from 'lodash';
import { UserEntity } from '@wephone-core/model/entity/user';
import { EntityManager, ConfigManager } from '@wephone-core/wephone-core.module';
import { AgentRepository } from '@wephone-core/model/repository/agent';
import { FlexBaseComponent } from '@wephone-core-ui';
import { EavesdropService } from 'src/app/wephone-common/service/eavesdrop.service';
import { AgentService } from '@wephone/services/agent.service';
import { DialogService, regexSearch } from '@wephone-utils';
import { IAgentItemActionMenu } from '@wephone-common/user/agent-item/agent-item.i';
import { _ti } from '@wephone-translation';
import { FeatureName, UserType } from '@wephone-core/system';
import { EavesdropRequestEntity } from '@wephone-core/model/entity/eavesdrop_request';
import { AgentStatusType } from '@wephone/components/agents/agent-status-type';

@Component({
  selector: 'phone-available-agents',
  templateUrl: 'phone-available-agents.html',
  styleUrls: ['phone-available-agents.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class PhoneAvailableAgentsComponent extends FlexBaseComponent implements OnInit {
  PERIODICAL_UPDATE: true;
  @Input() header: string;
  @Input() headerStyle?: any;
  @Input() searchText: string;
  @Input() displayGroup: boolean;

  currentUser: UserEntity;
  agent_list: AgentEntity[] = [];
  agentActions: IAgentItemActionMenu[];
  eavesdrop_list: EavesdropRequestEntity[] = [];
  availableAgentsOrdered: AgentEntity[] = [];
  displayContent = true;

  constructor(
    protected readonly em: EntityManager,
    protected readonly authService: AuthenticationService,
    protected readonly configManager: ConfigManager,
    protected readonly agentService: AgentService,
    protected readonly eavesdropService: EavesdropService,
    protected readonly dialogService: DialogService,
    cdr: ChangeDetectorRef,
  ) {
    super(cdr);
  }

  ngOnInit(): any {
    super.ngOnInit();
    this.currentUser = this.authService.getUser();
    this.agent_list = this.em.getRepository<AgentRepository>('AgentRepository').getObjectList();
    this.setActionMenu();
  }

  detectChanges(): void {
    this.setActionMenu();
    super.detectChanges();
  }

  private getAvailableAgents(): AgentEntity[] {
    return this.agent_list.filter((i: AgentEntity) => {
      return this.filter_agent(i)
        && this.search_agent(i);
    });
  }

  get has_available_agent(): boolean {
    this.availableAgentsOrdered = _.orderBy(
      this.getAvailableAgents(),
      ['name'],
      ['asc'],
    );
    return this.availableAgentsOrdered.length > 0;
  }

  private filter_agent(agent: AgentEntity): boolean {
    if (!this.currentUser) {
      return false;
    }

    if (!agent.enabled) {
      return false;
    }

    if (agent.user && agent.user.user_type === UserType.ACCOUNTANT) {
      return false;
    }

    const current_user_group_ids: number[] = this.currentUser.group_ids || [];
    if (
      this.authService.isWatcher()
      && (this.configManager.hasFeature(FeatureName.SERVICE_GROUP)
        && agent.group_ids
        && current_user_group_ids.length
        && !agent.group_ids.filter((n: number) => {
          return current_user_group_ids.indexOf(n) !== -1;
        }).length)
    ) {
      return false;
    }

    // not show agent in case of calling from sipphone to sipphone
    if (!agent.is_online_cc && !agent.reserved) {
      return false;
    }

    if (agent.agent_status !== AgentStatusType.AVAILABLE) {
      return false;
    }

    return true;
  }

  private search_agent(agent: AgentEntity): boolean {
    if (!this.searchText) {
      return true;
    }

    // const re = new RegExp(this.searchText, 'i');
    const searchTextList = [agent.name];
    for (const queue of agent.inbound_queue_list) {
      searchTextList.push(queue.queue_name);
    }

    for (const searchText of searchTextList) {
      // if (searchText.search(re) !== -1) {
      if (regexSearch(searchText, this.searchText)) {
        return true;
      }
    }

    return false;
  }

  private setActionMenu(): void {
    this.agentActions = [
      {
        id: 'eavsedrop-agent',
        icon: 'headphones',
        label: 'livecall.actions.eavesdrop',
        callback: (agent: AgentEntity) => {
          return this.handleEavesdropAgent(agent);
        },
        visible: (agent: AgentEntity) => {
          return this.eavesdropService.canEavesdropAgent(this.currentUser, agent);
        }
      },
      {
        id: 'remove-eavesdrop-agent',
        icon: 'headset_off',
        color: 'warn',
        label: 'livecall.eavesdrop.remove',
        callback: (agent: AgentEntity) => {
          return this.handleRemoveEavesdropAgent(agent);
        },
        visible: (agent: AgentEntity) => {
          return this.eavesdropService.canRemoveEavesdrop(this.currentUser, agent);
        }
      },
    ];
  }

  async handleEavesdropAgent(agent: AgentEntity): Promise<void> {
    await this.eavesdropService.call_eavesdrop_agent(agent.user.id);
    this.detectChanges();
  }

  async handleRemoveEavesdropAgent(agent: AgentEntity): Promise<void> {
    const eavesdropAgent: EavesdropRequestEntity = this.eavesdropService.getEavesdropAgent(this.currentUser, agent);
    if (!eavesdropAgent) {
      this.toastService.showError('Eavesdrop not found');
      return;
    }

    await this.eavesdropService.removeEavesdropRequest(eavesdropAgent.id);
    this.toastService.showSuccess(_ti('public.message.delete_success'));
    this.detectChanges();
  }
}
